import React, { useState, useEffect } from 'react';
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useParams, useNavigate } from 'react-router-dom';
import ImageManagement from '../components/ImageManagement';
import { getCharacterImages, uploadCharacterImage, listUserCharacters } from '../utils/characterUtils';
import { getUserId } from '../utils/userUtils';

const ImageManagementPage = () => {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  
  const [character, setCharacter] = useState(null);
  const [currentImages, setCurrentImages] = useState({
    avatar_url: "",
    selfie_images: [],
    total_selfie_size: 0
  });
  const [loading, setLoading] = useState(true);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    const loadCharacterData = async () => {
      try {
        const userId = getUserId(user);
        if (!userId) {
          throw new Error("User ID not available");
        }

        // Get user's characters
        const characters = await listUserCharacters(userId);
        const foundCharacter = characters.find(char => char.character_id === characterId);
        
        if (!foundCharacter) {
          throw new Error("Character not found or you don't have permission to edit it");
        }

        setCharacter(foundCharacter);

        // Load current images
        const images = await getCharacterImages(characterId, userId);
        setCurrentImages(images);

      } catch (error) {
        console.error('Error loading character data:', error);
        alert(`Error: ${error.message}`);
        navigate('/'); // Redirect to home if error
      } finally {
        setLoading(false);
      }
    };

    if (characterId && user) {
      loadCharacterData();
    }
  }, [characterId, user, navigate]);

  const handleImageUpload = async (imageData, type) => {
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      setUploadingImages(true);
      await uploadCharacterImage(characterId, userId, imageData, type);
      
      // Refresh current images
      const updatedImages = await getCharacterImages(characterId, userId);
      setCurrentImages(updatedImages);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Error uploading ${type}: ${error.message}`);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageDelete = async (image, type, index) => {
    try {
      // Note: This would require a backend endpoint for image deletion
      console.log(`Delete ${type} image:`, image, 'at index:', index);
      alert('Image deletion functionality would be implemented here');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`Error deleting ${type}: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading character data...</p>
        </div>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-red-400 mb-4">Character not found</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-blue-400 hover:text-blue-300 mb-2 flex items-center gap-2"
            >
              ← Back
            </button>
            <h1 className="text-3xl font-bold text-white">Image Management</h1>
            <p className="text-gray-400">
              Managing images for: <span className="text-white font-medium">{character.name}</span>
            </p>
          </div>
        </div>

        {/* Image Management Interface */}
        <div className="bg-gray-800 rounded-lg p-6">
          {uploadingImages && (
            <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg p-3 mb-6">
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                <span className="text-blue-200 text-sm">Processing images...</span>
              </div>
            </div>
          )}
          
          <ImageManagement
            currentAvatar={currentImages.avatar_url}
            currentSelfies={currentImages.selfie_images}
            onImageUpload={handleImageUpload}
            onImageDelete={handleImageDelete}
            totalSelfieSize={currentImages.total_selfie_size}
          />
        </div>

        {/* Help Section */}
        <div className="bg-gray-800 rounded-lg p-6 mt-6">
          <h2 className="text-xl font-semibold text-white mb-4">How to Use</h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-300">
            <div>
              <h3 className="font-medium text-white mb-2">Bulk Upload</h3>
              <ul className="space-y-1">
                <li>• Click "Bulk Upload" to select multiple images</li>
                <li>• Drag and drop images directly</li>
                <li>• Preview all images before uploading</li>
                <li>• Remove individual images from selection</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-white mb-2">Compression</h3>
              <ul className="space-y-1">
                <li>• Images over size limits are auto-compressed</li>
                <li>• Large dimensions are optimized</li>
                <li>• Green checkmarks show compressed images</li>
                <li>• Quality maintained while reducing size</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-white mb-2">Storage Limits</h3>
              <ul className="space-y-1">
                <li>• Avatar: 1MB maximum</li>
                <li>• Selfies: 5MB each, 20MB total</li>
                <li>• Up to 10 selfie images</li>
                <li>• Visual storage tracking</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-white mb-2">Supported Formats</h3>
              <ul className="space-y-1">
                <li>• PNG, JPG, JPEG</li>
                <li>• GIF (static images)</li>
                <li>• Automatic format validation</li>
                <li>• Full-screen preview available</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageManagementPage;